import { api } from "./apiSlice";

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

export interface UserProfile {
  id: string;
  email: string;
  name: string;
  bloodGroup: string;
  totalDonations: number;
  totalPoints: number;
  profileImageUrl: string | null;
  isActive: boolean;
  createdAt: string;
  userDetails: {
    address: string;
    city: string;
    district: string;
    phoneNumber: string | null;
    emergencyContact: string | null;
  } | null;
}

export interface UpdateUserProfileInput {
  name?: string;
  profileImageUrl?: string;
  address?: string;
  city?: string;
  district?: string;
  phoneNumber?: string;
  emergencyContact?: string;
}

export interface UserStats {
  totalDonations: number;
  totalPoints: number;
  donationBadge: string;
  nextEligible: string | null;
}

export interface BadgeThresholdInfo {
  badge: string;
  threshold: number;
  name: string;
  color: string;
  icon: string;
  gradient: [string, string];
  description: string;
}

export interface UserBadgeInfo {
  currentBadge: BadgeThresholdInfo;
  nextBadge: { nextBadge: string; donationsNeeded: number; threshold: number } | null;
  totalDonations: number;
  allBadges: BadgeThresholdInfo[];
  emergencyResponderBadge: boolean;
}

export interface AuditLogEntry {
  id: string;
  timestamp: string;
  action: string;
  userName: string | null;
}

export interface UserSearchResult {
  id: string;
  name: string;
  nic: string;
  email: string;
  bloodGroup: string;
  totalDonations: number;
  profileImageUrl: string | null;
  isActive: boolean;
  district: string | null;
  city: string | null;
  phoneNumber: string | null;
}

export const usersApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getUserProfile: builder.query<UserProfile, void>({
      query: () => "/users/profile",
      transformResponse: (res: ApiResponse<UserProfile>) => res.data,
      providesTags: ["Users"],
    }),
    updateUserProfile: builder.mutation<UserProfile, UpdateUserProfileInput>({
      query: (body) => ({ url: "/users/profile", method: "PUT", body }),
      transformResponse: (res: ApiResponse<UserProfile>) => res.data,
      invalidatesTags: ["Users"],
    }),
    getUserStats: builder.query<UserStats, void>({
      query: () => "/users/stats",
      transformResponse: (res: ApiResponse<UserStats>) => res.data,
      providesTags: ["Users"],
    }),
    getUserBadgeInfo: builder.query<UserBadgeInfo, string>({
      query: (userId) => `/users/${userId}/badge-info`,
      transformResponse: (res: ApiResponse<UserBadgeInfo>) => res.data,
    }),
    getAuditLogs: builder.query<AuditLogEntry[], { limit?: number } | void>({
      query: (args) => `/users/audit-logs${args?.limit ? `?limit=${args.limit}` : ""}`,
      transformResponse: (res: ApiResponse<{ logs: AuditLogEntry[] }>) => res.data.logs,
    }),
    searchUsers: builder.query<UserSearchResult[], { q?: string; bloodGroup?: string; limit?: number } | void>({
      query: (args) => {
        const params = new URLSearchParams();
        if (args?.q) params.set("q", args.q);
        if (args?.bloodGroup) params.set("bloodGroup", args.bloodGroup);
        if (args?.limit) params.set("limit", String(args.limit));
        return `/users/search?${params.toString()}`;
      },
      transformResponse: (res: ApiResponse<{ users: UserSearchResult[] }>) => res.data.users,
    }),
  }),
});

export const {
  useGetUserProfileQuery,
  useUpdateUserProfileMutation,
  useGetUserStatsQuery,
  useGetUserBadgeInfoQuery,
  useGetAuditLogsQuery,
  useSearchUsersQuery,
} = usersApi;
